import { Component, Input } from '@angular/core';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { NotificationQueueItem } from '../data-access/notification-queue.models';
import { HoverIdComponent } from '@clinic-platform/ui-components';

@Component({
  selector: 'app-notification-queue-table',
  standalone: true,
  imports: [HoverIdComponent, StatusBadgeComponent, TranslatePipe],
  template: `
    <div class="notification-queue-table">
      <table class="notification-queue-table__table">
        <thead>
          <tr>
            <th>{{ 'Event Type' | t }}</th>
            <th>{{ 'Channel' | t }}</th>
            <th>{{ 'Source' | t }}</th>
            <th>{{ 'Recipient Email' | t }}</th>
            <th>{{ 'common.status' | t }}</th>
            <th>{{ 'Created At' | t }}</th>
            <th>{{ 'Updated At' | t }}</th>
          </tr>
        </thead>

        <tbody>
          @for (item of items; track item.id) {
            <tr>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <span class="notification-queue-table__clip">{{ humanizeKey(item.eventType) }}</span>
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <span class="notification-queue-table__clip">{{ humanizeKey(item.channel) }}</span>
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <span class="notification-queue-table__clip">{{ humanizeKey(item.entityType) }}</span>
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <span class="notification-queue-table__clip">{{ item.recipientEmail ?? '—' }}</span>
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <app-status-badge
                    [label]="getStatusViewModel(item.status).label"
                    [tone]="getStatusViewModel(item.status).tone"
                  />
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <span class="notification-queue-table__clip">{{ formatAsLocalNaiveDateTime(item.createdAt) }}</span>
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="item.entityId">
                  <span class="notification-queue-table__clip">{{ formatAsLocalNaiveDateTime(item.updatedAt) }}</span>
                </lib-hover-id>
              </td>
            </tr>
          }
        </tbody>
      </table>

      <div class="notification-queue-table__cards">
        @for (item of items; track item.id) {
          <article class="notification-queue-table__card">
            <div class="notification-queue-table__card-head">
              <div class="notification-queue-table__card-title">{{ humanizeKey(item.eventType) }}</div>
              <app-status-badge
                [label]="getStatusViewModel(item.status).label"
                [tone]="getStatusViewModel(item.status).tone"
              />
            </div>

            <div class="notification-queue-table__card-grid">
              <div class="notification-queue-table__card-row">
                <div class="notification-queue-table__card-label">{{ 'Channel' | t }}</div>
                <div class="notification-queue-table__card-value">{{ humanizeKey(item.channel) }}</div>
              </div>

              <div class="notification-queue-table__card-row">
                <div class="notification-queue-table__card-label">{{ 'Source' | t }}</div>
                <div class="notification-queue-table__card-value">{{ humanizeKey(item.entityType) }}</div>
              </div>

              <div class="notification-queue-table__card-row">
                <div class="notification-queue-table__card-label">{{ 'Recipient Email' | t }}</div>
                <div class="notification-queue-table__card-value">{{ item.recipientEmail ?? '—' }}</div>
              </div>

              <div class="notification-queue-table__card-row">
                <div class="notification-queue-table__card-label">{{ 'Created At' | t }}</div>
                <div class="notification-queue-table__card-value">{{ formatAsLocalNaiveDateTime(item.createdAt) }}</div>
              </div>

              <div class="notification-queue-table__card-row">
                <div class="notification-queue-table__card-label">{{ 'Updated At' | t }}</div>
                <div class="notification-queue-table__card-value">{{ formatAsLocalNaiveDateTime(item.updatedAt) }}</div>
              </div>
            </div>
          </article>
        }
      </div>
    </div>
  `,
  styles: [`
    .notification-queue-table {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow-x: auto;
    }

    .notification-queue-table__table {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      min-width: 1040px;
    }

    .notification-queue-table__table th:nth-child(1) {
      width: 20%;
    }

    .notification-queue-table__table th:nth-child(2) {
      width: 110px;
    }

    .notification-queue-table__table th:nth-child(3) {
      width: 16%;
    }

    .notification-queue-table__table th:nth-child(4) {
      width: 20%;
    }

    .notification-queue-table__table th:nth-child(5) {
      width: 120px;
    }

    .notification-queue-table__table th:nth-child(6),
    .notification-queue-table__table th:nth-child(7) {
      width: 150px;
    }

    .notification-queue-table__cards {
      display: none;
    }

    .notification-queue-table__table th,
    .notification-queue-table__table td {
      text-align: left;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
      font-size: 13px;
    }

    .notification-queue-table__table th {
      background: #f9fafb;
      color: #374151;
    }

    .notification-queue-table__table td {
      color: #111827;
    }

    .notification-queue-table__clip {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      vertical-align: top;
      white-space: nowrap;
    }

    .notification-queue-table__card {
      display: grid;
      gap: 12px;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
    }

    .notification-queue-table__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .notification-queue-table__card-title {
      color: #111827;
      font-size: 15px;
      font-weight: 700;
      overflow-wrap: anywhere;
    }

    .notification-queue-table__card-grid {
      display: grid;
      gap: 10px;
    }

    .notification-queue-table__card-row {
      display: grid;
      grid-template-columns: minmax(0, 96px) minmax(0, 1fr);
      gap: 10px;
      align-items: start;
    }

    .notification-queue-table__card-label {
      color: #64748b;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .notification-queue-table__card-value {
      color: #111827;
      font-size: 13px;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }

    @media (max-width: 900px) {
      .notification-queue-table {
        padding: 10px;
        display: grid;
        gap: 12px;
        background: #f8fafc;
      }

      .notification-queue-table__table {
        display: none;
      }

      .notification-queue-table__cards {
        display: grid;
        gap: 12px;
      }
    }

    @media (max-width: 640px) {
      .notification-queue-table__card-row {
        grid-template-columns: 1fr;
        gap: 4px;
      }
    }
  `]
})
export class NotificationQueueTableComponent
{
  @Input()
  public items: NotificationQueueItem[] = [];

  protected getStatusViewModel(status: NotificationQueueItem['status']): StatusBadgeViewModel
  {
    switch (status)
    {
      case 'pending':
        return { label: 'status.pending', tone: 'warning' };

      case 'processing':
        return { label: 'status.processing', tone: 'info' };

      case 'sent':
        return { label: 'status.sent', tone: 'success' };

      case 'failed':
        return { label: 'status.failed', tone: 'danger' };

      default:
        return { label: status, tone: 'neutral' };
    }
  }

  protected humanizeKey(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    const withSpaces = normalized
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  protected formatAsLocalNaiveDateTime(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) {
      return normalized;
    }

    const [, year, month, day, hours, minutes] = match;
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }
}
